import React, { useRef, useEffect } from 'react'
import { Box } from '../Box/Box'
import { Stack } from '../Stack/Stack'
import { Inline } from '../Inline/Inline'
import { Heading } from '../Heading/Heading'
import { Text } from '../Text/Text'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'
import styled from '@emotion/styled'
import { use100vh } from 'react-div-100vh'

export interface CardProps {
  children?: React.ReactNode
  title?: string
  caption?: string
  radius?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl'
  inset?: ResponsiveProp<'xs' | 's' | 'm' | 'l' | 'xl' | 'none'>
  insetSquish?: 'xs' | 's' | 'm' | 'l' | 'xl'
  insetStretch?: 'xs' | 's' | 'm' | 'l' | 'xl'
  overflow?: 'scroll' | 'visible' | 'hidden'
}

export const Card: React.FC<CardProps> = ({
  children,
  inset,
  insetSquish,
  insetStretch = 'l',
  radius = 'm',
  title,
  caption,
  overflow = 'visible',
  ...props
}) => {
  const device = useScreenSize()
  const viewPortHeight = use100vh()
  const maxHeight = viewPortHeight ? viewPortHeight - 32 : undefined

  inset = useResponsiveProp(inset, device)

  const cardContent = (
    <Box
      inset={inset ?? undefined}
      insetSquish={insetSquish ?? undefined}
      insetStretch={!inset && !insetSquish && insetStretch ? insetStretch : undefined}
    >
      <Stack>
        {(title || caption) && (
          <Box inset="m">
            <Stack space="xs">
              <Heading level={3}>{title}</Heading>
              {caption && (
                <Text size="small" tone="light">
                  {caption}
                </Text>
              )}
            </Stack>
          </Box>
        )}
        {children}
      </Stack>
    </Box>
  )

  return (
    <Box
      background="var(--card--background)"
      radius={radius}
      overflow={overflow === 'scroll' ? 'hidden' : overflow}
      maxHeight={overflow === 'scroll' ? maxHeight : undefined}
      display={overflow === 'scroll' ? 'flex' : 'block'}
      {...props}
    >
      {overflow === 'scroll' ? <ScrollableContent>{cardContent}</ScrollableContent> : cardContent}
    </Box>
  )
}

export interface MetricsCardProps {
  label: string
  metric: string | number
  percent?: string | number
  tone?: 'light' | 'neutral' | 'action' | 'positive' | 'negative' | 'caution' | 'contrast'
}

export const MetricsCard = ({ label, metric, percent, tone = 'neutral' }: MetricsCardProps) => (
  <Box
    background="var(--card--background)"
    border="1px solid var(--card--border)"
    radius="m"
    inset="m"
  >
    <Stack space="s">
      <Text tone="light">{label}</Text>
      <Inline space="xs" alignY="bottom" nowrap>
        <Text size="huge" tone={tone} weight="fat">
          {metric}
        </Text>
        {percent && (
          <Text size="bigger" tone="action" weight="stronger">
            {percent}%
          </Text>
        )}
      </Inline>
    </Stack>
  </Box>
)

const ScrollableContent = ({ children }: { children: React.ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0)
    }
  }, [])

  return <StyledScrollableContent ref={scrollRef}>{children}</StyledScrollableContent>
}

const StyledScrollableContent = styled.div`
  overflow: auto;
  flex-grow: 0;
`
